// InstallPrompt.jsx
import React, { useEffect, useState } from "react";

function isStandalone() {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  const displayModeStandalone =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(display-mode: standalone)").matches;

  const iosStandalone = !!navigator.standalone;

  return displayModeStandalone || iosStandalone;
}

export default function InstallPrompt({
  label = "Install App",
  className = "",
  style = {},
  buttonStyle = {},
  showIcon = false,
  icon = null,
  onInstalled = null,
}) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [installAttempted, setInstallAttempted] = useState(false);

  useEffect(() => {
    const syncInstalledState = () => {
      const installed = isStandalone();
      setIsInstalled(installed);

      if (installed) {
        setDeferredPrompt(null);
        window.deferredPrompt = null;
      }
    };

    syncInstalledState();

    // Recover prompt if it was already captured earlier
    if (window.deferredPrompt) {
      setDeferredPrompt(window.deferredPrompt);
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();

      // Store globally so it survives navigation / rerenders
      window.deferredPrompt = e;
      setDeferredPrompt(e);
      setInstallAttempted(false);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setInstallAttempted(false);
      window.deferredPrompt = null;

      if (typeof onInstalled === "function") {
        onInstalled();
      }
    };

    const handleVisibilityChange = () => {
      syncInstalledState();

      if (window.deferredPrompt) {
        setDeferredPrompt(window.deferredPrompt);
      }
    };

    const handleFocus = () => {
      syncInstalledState();

      if (window.deferredPrompt) {
        setDeferredPrompt(window.deferredPrompt);
      }
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [onInstalled]);

  const handleInstallClick = async () => {
    // Always re-check installed state first
    if (isStandalone()) {
      setIsInstalled(true);
      setDeferredPrompt(null);
      window.deferredPrompt = null;

      if (typeof onInstalled === "function") {
        onInstalled();
      }
      return;
    }

    const promptEvent = window.deferredPrompt || deferredPrompt;

    if (!promptEvent) {
      setInstallAttempted(true);
      console.log("Install prompt not available yet");
      return;
    }

    try {
      await promptEvent.prompt();
      await promptEvent.userChoice;
    } catch (err) {
      console.error("Install prompt error:", err);
    } finally {
      // Chrome requires clearing the event after use
      window.deferredPrompt = null;
      setDeferredPrompt(null);
      setInstallAttempted(true);

      // Re-check after prompt flow completes
      const installed = isStandalone();
      setIsInstalled(installed);

      if (installed && typeof onInstalled === "function") {
        onInstalled();
      }
    }
  };

  if (isInstalled) return null;

  return (
    <div
      className={className}
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "8px",
        ...style,
      }}
    >
      <button
        type="button"
        onClick={handleInstallClick}
        aria-disabled="false"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          padding: "10px 14px",
          borderRadius: "999px",
          border: "none",
          cursor: "pointer",
          fontWeight: 600,
          lineHeight: 1.2,
          opacity: 1,
          ...buttonStyle,
        }}
      >
        {showIcon && icon ? <span>{icon}</span> : null}
        <span>{label}</span>
      </button>

      {installAttempted && !deferredPrompt ? null : null}
    </div>
  );
}
